import api from './api';

/**
 * ORDER-CONTENT-01 — one reading of `GET /patient/payment/eligibility`.
 *
 * The dashboard and the order screen must agree on what "payable now" means,
 * and neither may present a failed read as "paid". The canonical gate already
 * answers every case; this only names them:
 *
 *   payable    — a chargeable obligation exists right now
 *   settled    — the contract is fully paid
 *   scheduled  — an authoritative future date / release condition says so
 *   activation_pending — the predecessor is paid but the next obligation was
 *                never released; the schedule stalled and needs recovery
 *   incomplete — the schedule is missing or inconsistent; blocked, retryable
 *   blocked    — accepted, but no payment provider is active
 *   none       — this order cannot be paid (not accepted / not the owner)
 *   unknown    — the eligibility read itself failed; say so, never "paid"
 *
 * PAYMENT-PLAN-STATE-01: a 409 `payment_obligation_unavailable` proves only
 * that nothing is chargeable right now — it is NOT evidence of a future
 * instalment, so it may never be rendered as "scheduled later". The gate now
 * classifies the plan itself and answers `plan_state`.
 *
 * PLAN-ACTIVATION-STALL-01: a settled predecessor with an unreleased
 * successor is a stalled activation, shown as such and retryable — never as
 * normal future scheduling.
 *
 * Read-only: it never initiates a payment.
 */
export const fetchOrderPaymentState = async (orderId) => {
  try {
    const { data } = await api.get('/patient/payment/eligibility', {
      params: { order_id: orderId },
    });
    const plan = data?.plan_state;
    if (plan === 'settled' || data?.fully_settled) return { state: 'settled', data };
    if (data?.eligible && data?.payment_enabled === false) return { state: 'blocked', data };
    if (data?.eligible) return { state: 'payable', data };
    if (plan === 'scheduled') return { state: 'scheduled', data };
    if (plan === 'activation_pending') return { state: 'activation_pending', data };
    if (plan === 'obligations_missing' || plan === 'obligations_inconsistent') {
      return { state: 'incomplete', data };
    }
    return { state: 'unknown', data };
  } catch (err) {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    const code = typeof detail === 'object' ? detail?.code : null;
    if (status === 403) return { state: 'none', data: null };
    if (status === 409 && code === 'IDENTITY_AMBIGUOUS') {
      return { state: 'blocked_identity', data: null, code };
    }
    return { state: 'unknown', data: null, code };
  }
};

export const isPayableNow = (entry) => entry?.state === 'payable';

export const paymentLinkFor = (orderId, quoteId) => {
  const params = new URLSearchParams();
  if (quoteId) params.set('quote_id', quoteId);
  if (orderId) params.set('order_id', orderId);
  return `/patient/payment?${params.toString()}`;
};
